import React, { useState, useCallback, useEffect } from 'react';
import { HStack } from 'native-base';
import { faAngleLeft, faStar } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Image as ImageProps, useI18n, useTheme, i18nFn, resizedUrl } from '@springbig/shared';
import Image from './Image';
import { SpIcon } from './Icon';
import { Caption, H1, H2 } from './Typography';
import { Button, Modal } from '~/shared/Theme';
import CopyButton from './CopyButton';

dayjs.extend(relativeTime);

export type RedeemedRewardProps = {
    memberPhoneNumber: string;
    createdAt: string;
    isOpen: boolean;
    title: string;
    image?: ImageProps[];
    description: string;
    expires?: string | null;
    confirmationNumber: number | string;
    isExternalSource?: boolean;
    isInStore?: boolean;
    returnWallet: () => void;
};

const RedeemedRewardModal = (
    {
        image,
        memberPhoneNumber,
        createdAt,
        isOpen,
        title,
        returnWallet,
        expires,
        confirmationNumber,
        isExternalSource,
        isInStore
    }: RedeemedRewardProps) => {

    const { t } = useI18n();

    const redeemedImage = image && image.length > 0  && image.filter(img => img.label === 'coupon');

    const { primary, radius_md, button_text } = useTheme();

    const copyButtonLabel = t('rewards.external_source.copy_to_clipboard');

    return (
        <Modal isOpen={isOpen}>
            <Modal.Header textAlign={'center'}>
                <H2>{ memberPhoneNumber }</H2>

                <H2 text={'redeem.redeemed_at'} textValues={[dayjs(createdAt).format('h:mm:ss a')]} />

                {
                    expires && <ShowCounter expirationTime={expires} t={t} />
                }

                <H1>{ title }</H1>
            </Modal.Header>
            <Modal.Body textAlign={'center'} alignItems={'center'}>
                {
                    (image && image.length) || (redeemedImage && redeemedImage.length) > 0 ? (
                        <Image
                            uri={redeemedImage && redeemedImage.length > 0 ? resizedUrl(redeemedImage[0].url, 160, 160) : resizedUrl(image[0].url, 160, 160)}
                            w={40}
                            h={40}
                            rounded={radius_md}
                        />
                    ) : <SpIcon color={primary} icon={faStar} size={180} />
                }

                <H2 text={'redeem.redeemed'} />

                { expires &&
                    <Caption
                        text={'redeem.valid_until'}
                        textValues={[`${dayjs(expires).format('h:mm a')}, ${dayjs(expires).format('dddd MMM DD')}`]}
                    />
                }

                { isExternalSource ? (
                    <>
                        <H2 mt={4} text={'redeem.promo_code'} textValues={[confirmationNumber]} />
                        { isInStore ? (
                            <Caption text={'redeem.show_to_staff_in_store'} />
                        ) : (
                            <CopyButton copyText={String(confirmationNumber)} buttonText={copyButtonLabel} />
                        )}
                    </>
                ) : (
                    <H2 mt={4} text={'redeem.confirmation'} textValues={[confirmationNumber]} />
                )}
            </Modal.Body>

            <Modal.Footer justifyContent={'center'}>
                <Button
                    onPress={returnWallet}
                    modifier={'unlocked'}
                    color={button_text}
                    px={8}
                >
                    <HStack space={4} alignItems={'center'}>
                        <SpIcon icon={faAngleLeft} />
                        <H1 text={'redeem.back'} />
                    </HStack>
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RedeemedRewardModal;

const ShowCounter = ({ expirationTime, t }: { expirationTime: string, t: i18nFn }) => {

    const [time] = useCountdown({ expirationTime });
    const allTimesExist =
      time?.days !== undefined &&
      time?.hours !== undefined &&
      time?.minutes !== undefined &&
      time?.seconds !== undefined;

    return (
        <H2 my={0}>
            {time?.expired ? (
                t('redeem.expired')
            ) : allTimesExist ? (
                <>
                    {t('redeem.expires')}
                    {` ${time.days}d`}
                    {` ${time.hours}h`}
                    {` ${time.minutes}m`}
                    {` ${time.seconds}s`}
                </>
            ) : (
                ''
            )}
        </H2>
    );
};

type CountDownTimeProps = {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired?: boolean;
}

const useCountdown = ({ expirationTime }: { expirationTime: string }) => {

    const [time, setTime] = useState<CountDownTimeProps>();

    const handleCalculateDifference = useCallback((expiredAtDate?: string) => {

        const created = new Date().getTime();
        const expired = new Date(expiredAtDate!).getTime();

        const _second = 1000;
        const _minute = _second * 60;
        const _hour = _minute * 60;
        const _day = _hour * 25;

        const distance = expired - created;

        const days = Math.floor(distance / _day);
        const hours = Math.floor((distance % _day) / _hour);
        const minutes = Math.floor((distance % _hour) / _minute);
        const seconds = Math.floor((distance % _minute) / _second);

        if (distance <= 0) {
            return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
        }

        return {
            days,
            hours,
            minutes,
            seconds
        };
    }, []);

    useEffect(() => {

        if (expirationTime) {

            const interval = setInterval(() => {

                setTime(handleCalculateDifference(expirationTime));
            }, 1000);

            return () => {

                clearInterval(interval);
            };
        }
    }, [expirationTime, handleCalculateDifference]);

    return [time];
};
